import type { ReactNode } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { Spinner } from '../Spinner';

interface Props {
  src: string;
  className?: string;
  /**
   * Shown whenever the photo is not visible: missing `src`, still loading, or failed to load.
   * If omitted, a spinner is used while a `src` is set (Radix shows fallback until load completes or errors).
   */
  emptyFallback?: ReactNode;
}

const neutralPlaceholder = <span className="block size-full bg-gray-100" aria-hidden />;

export default function MianAvatar({ src, className, emptyFallback }: Props) {
  const hasSrc = Boolean(src?.trim());

  /** Radix keeps the fallback visible while loading and on error; use `emptyFallback` for all of those when provided. */
  const fallbackContent = !hasSrc ? (
    emptyFallback ?? neutralPlaceholder
  ) : emptyFallback ? (
    emptyFallback
  ) : (
    <Spinner />
  );

  return (
    <Avatar
      className={`inline-flex select-none  items-center justify-center overflow-hidden rounded-full align-middle ${className}`}>
      {hasSrc && (
        <AvatarImage className="size-full rounded-[inherit] object-cover" src={src} alt="avatar" />
      )}
      <AvatarFallback
        delayMs={hasSrc ? undefined : 0}
        className="leading-1 flex size-full items-center justify-center bg-gray-50 text-[15px] font-medium text-violet11">
        {fallbackContent}
      </AvatarFallback>
    </Avatar>
  );
}
